import { yupResolver } from '@hookform/resolvers/yup'
import clsx from 'clsx'
import Image from 'next/image'
import Trans from 'next-translate/Trans'
import useTranslation from 'next-translate/useTranslation'
import { useState } from 'react'
import { Controller, FormProvider, useForm } from 'react-hook-form'
import { useMutation } from 'react-query'
import ReactSelect from 'react-select'
import * as yup from 'yup'

import Checkbox from '@/atoms/Checkbox'
import Field from '@/atoms/Field'
import { EMPLOYEES } from '@/constants/employees'

import { submitContactWellBeingForm } from '../logic/submitContactWellBeingForm'
import { customStyles } from '../scss/selectStyles'
import { CheckboxId, WellBeingContactForm } from '../types'

function Contact() {
  const { t } = useTranslation('wellBeing')
  const [checked, setChecked] = useState({ 1: false, 2: false })

  const { isLoading, mutate } = useMutation(submitContactWellBeingForm)

  const allChecked = Object.values(checked).every(Boolean)
  const form = useForm<WellBeingContactForm>({ resolver })

  const errorName = form.formState.errors.name?.message
  const errorCompany = form.formState.errors.company?.message
  const errorEmail = form.formState.errors.email?.message
  const errorPhone = form.formState.errors.phone?.message
  const errorBody = form.formState.errors.body?.message

  const handleChange = (id: CheckboxId) => {
    setChecked((prev) => ({
      ...prev,
      [id]: !prev[id],
    }))
  }

  return (
    <div className="flex flex-col items-center">
      <div className="flex flex-col items-center lg:flex-row lg:p-8">
        <div className="flex mb-12 space-x-12 lg:hidden">
          <div className="flex items-start justify-end w-1/2">
            <div className="w-32 2xs:w-36 2sm:w-40 sm:w-40 2md:w-64 md:w-80 transform scale-x-[-1] -mt-8 2sm:-mt-10">
              <Image
                alt={t('contact_1')}
                src={`/images/well-being/contact-1.png`}
                height="100%"
                width="100%"
                layout="responsive"
                quality={75}
                loading="eager"
              />
            </div>
          </div>
          <div className="w-1/2">
            <div className="w-32 2xs:w-40 2sm:w-44 sm:w-48 2md:w-72 md:w-96">
              <Image
                alt={t('contact_2')}
                src={`/images/well-being/contact-2.png`}
                height="100%"
                width="100%"
                layout="responsive"
                quality={75}
                loading="eager"
              />
            </div>
          </div>
        </div>
        <div className="hidden lg:block">
          <div className="mt-32 -mr-4 w-52 xl:w-56">
            <Image
              alt={t('contact_1')}
              src={`/images/well-being/contact-1.png`}
              height="90%"
              width="110%"
              layout="responsive"
              quality={75}
              loading="eager"
            />
          </div>
        </div>
        <div className="mb-8 m-2 w-11/12 lg:w-[560px] xl:w-[700px] 2xl:w-[900px] 3xl:w-[1200px] shadow-md flex flex-col rounded-3xl">
          <div className="flex items-center justify-center w-full h-12 bg-gradient-to-r from-violet-60 to-violet-225 rounded-tl-3xl rounded-tr-3xl">
            <p className="text-lg font-black text-white uppercase lg:text-2xl">
              {t('form_title')}
            </p>
          </div>
          <div className="flex flex-col items-center p-2 md:p-0 md:mb-8 md:px-8">
            <p className="mt-2 mb-4 text-sm font-medium text-center lg:text-base text-violet-225">
              {t('form_copy')}
            </p>
            <FormProvider {...form}>
              <form
                onSubmit={form.handleSubmit((data) => {
                  mutate(data), form.reset()
                })}
                className="flex flex-col w-full p-2 md:gap-6 md:flex-row"
              >
                <div className="flex flex-col space-y-3 md:w-1/2">
                  <Field label="full_name" errorMessage={errorName}>
                    <Field.WellBeingTextInput {...form.register('name')} />
                  </Field>
                  <Field label="company" errorMessage={errorCompany}>
                    <Field.WellBeingTextInput {...form.register('company')} />
                  </Field>
                  <Field label="email_company" errorMessage={errorEmail}>
                    <Field.WellBeingTextInput {...form.register('email')} />
                  </Field>
                  <Field label="phone_number" errorMessage={errorPhone}>
                    <Field.WellBeingTextInput {...form.register('phone')} />
                  </Field>
                  <Field label="employees" className="pt-3">
                    <Controller
                      name="employees"
                      render={({ field }) => (
                        <ReactSelect
                          onChange={(newValue) =>
                            field.onChange(newValue?.value)
                          }
                          components={{
                            IndicatorSeparator: () => null,
                          }}
                          placeholder=""
                          options={Object.entries(EMPLOYEES).map(
                            ([, value]) => ({
                              value: value,
                              label: value,
                            })
                          )}
                          styles={customStyles}
                        />
                      )}
                    />
                  </Field>
                </div>
                <div className="flex flex-col mt-6 md:mt-0 md:w-1/2">
                  <Field
                    label="message_content"
                    className="w-full"
                    errorMessage={errorBody}
                  >
                    <Field.WellBeingTextarea
                      rows={8}
                      {...form.register('body')}
                    />
                  </Field>
                  <div className="flex items-center justify-center w-full">
                    <button
                      type="submit"
                      disabled={!allChecked}
                      className={clsx(
                        isLoading && 'bg-red-5',
                        !allChecked && 'cursor-not-allowed',
                        'mt-8 uppercase bg-green-60 text-white font-bold px-5 py-2 rounded-full opacity-70 w-1/2'
                      )}
                    >
                      {t('button_send')}
                    </button>
                  </div>
                </div>
              </form>
            </FormProvider>
          </div>
        </div>
        <div className="hidden lg:block">
          <div className="w-56 mt-40 -ml-4 lg:w-52 xl:w-60">
            <Image
              alt={t('contact_2')}
              src={`/images/well-being/contact-2.png`}
              height="90%"
              width="110%"
              layout="responsive"
              quality={75}
              loading="eager"
            />
          </div>
        </div>
      </div>
      <div className="flex flex-col items-center justify-center w-5/6 md:w-2/3">
        <div className="flex flex-col space-x-2 text-center">
          <p className="text-sm font-semibold md:text-base">
            {t('contact_form_copy_email')}
          </p>
          <a
            href="mailto:brands@brandbuddies.pl"
            target="_top"
            className="text-sm font-bold underline md:text-base"
          >
            {t('contact_form_copy_email_1')}
          </a>
        </div>
        <Trans
          i18nKey="wellBeing:contact_privacy_and_policy"
          components={[
            <p
              className="my-3 text-sm text-center text-black/80 md:text-base"
              key={0}
            />,
            <a
              key={1}
              href="/docs/privacy-policy.pdf"
              target="_blank"
              className="text-sm font-bold text-black md:text-base"
            />,
          ]}
        />
        <div className="flex flex-col space-y-3">
          {/* added copy instead of translation, to not refactor checkbox component */}
          <Checkbox
            id="checkbox1"
            checked={checked[1]}
            onChange={() => handleChange(1)}
            label="Wyrażam zgodę na przetwarzanie moich danych osobowych dla celów marketingowych i promocyjnych usług świadczonych przez Fansi sp. z o.o. z siedzibą w Warszawie (ul. Koszykowa 3 lok. 122, 00-564 Warszawa), który jest Administratorem Twoich danych osobowych."
          />
          {/* added copy instead of translation, to not refactor checkbox component */}
          <Checkbox
            id="checkbox2"
            checked={checked[2]}
            onChange={() => handleChange(2)}
            label="Wyrażam zgodę na przetwarzanie przez Fansi sp. z o.o. z siedzibą w Warszawie mojego adresu poczty elektronicznej, telefonu w celu przesyłania mi informacji handlowych dotyczących usług świadczonych przez Fansi sp. z o.o. z siedzibą w Warszawie (ul. Koszykowa 3 lok. 122, 00-564 Warszawa) za pomocą środków komunikacji elektronicznej, stosownie do treści przepisu art. 10 ust. 1 i 2 ustawy o świadczeniu usług drogą elektroniczną. Zgoda może być cofnięta w każdym czasie."
          />
        </div>
      </div>
    </div>
  )
}

const CONTACT_WELL_BEING_SCHEMA = yup.object().shape({
  name: yup.string().required('field.required'),
  company: yup.string().required('field.required'),
  email: yup.string().email('email.invalid').required('field.required'),
  phone: yup
    .string()
    .matches(/^[0-9]{9}$/, 'phone_number.invalid')
    .required('field.required'),
  employees: yup.string(),
  body: yup.string().required('field.required'),
})

const resolver = yupResolver(CONTACT_WELL_BEING_SCHEMA)

export default Contact
